import { Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { coucheModaleOuverte } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useAntiRebond } from '@/lib/anti-rebond';
import { useRechercheGlobale } from './api/recherche.api';
import { PaletteRecherche } from './PaletteRecherche';

/** Vrai sur un Mac : le raccourci s'y écrit ⌘K, ailleurs Ctrl+K. */
function surMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

/**
 * Raccourci d'ouverture : Ctrl+K, ⌘K sur Mac.
 *
 * Il se tait quand une couche modale est déjà ouverte. Ouvrir la palette
 * par-dessus un formulaire en cours de saisie proposerait de naviguer ailleurs,
 * donc d'abandonner ce qui est tapé — le contraire d'un raccourci utile.
 */
export function useRaccourciRecherche(ouvrir: () => void): void {
  const action = useRef(ouvrir);
  action.current = ouvrir;

  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'k') return;
      if (!e.ctrlKey && !e.metaKey) return;
      if (coucheModaleOuverte()) return;
      e.preventDefault();
      action.current();
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, []);
}

/** Le bouton visible : le raccourci ne s'apprend que s'il est écrit quelque part. */
export function DeclencheurRecherche({
  onOuvrir,
  className,
}: {
  onOuvrir: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onOuvrir}
      className={cn(
        'flex w-full items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/90',
        className,
      )}
    >
      <Search aria-hidden className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-left">Rechercher…</span>
      <kbd className="shrink-0 rounded border border-white/15 px-1 text-[10px] text-white/50">
        {surMac() ? '⌘K' : 'Ctrl K'}
      </kbd>
    </button>
  );
}

/**
 * Enveloppe la palette : garde le terme saisi et interroge l'API.
 *
 * Le terme est remis à zéro à chaque ouverture. Retrouver la recherche
 * précédente paraît serviable, mais on ouvre cette boîte pour chercher autre
 * chose — il faudrait effacer avant de taper, à chaque fois.
 */
export function RechercheGlobale({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const [terme, setTerme] = useState('');
  const termeDiffere = useAntiRebond(terme);
  const { data, isFetching } = useRechercheGlobale(termeDiffere);

  useEffect(() => {
    if (ouvert) setTerme('');
  }, [ouvert]);

  return (
    <PaletteRecherche
      ouvert={ouvert}
      onFermer={onFermer}
      terme={terme}
      onChangeTerme={setTerme}
      resultats={data?.resultats ?? []}
      /* En attente d'anti-rebond, `isFetching` est encore faux : sans ce
         second terme, la palette paraîtrait figée le temps de la pause. */
      chargement={isFetching || terme !== termeDiffere}
    />
  );
}
